import type { Messages } from "../messages";
import { plural } from "../types";

export const pl: Messages = {
  common: {
    copy: "Kopiuj",
    copied: "Skopiowano",
    optional: "Opcjonalne",
    show: "Pokaż",
    hide: "Ukryj",
    loading: "Wczytywanie…",
    on: "Wł.",
    off: "Wył.",
  },

  language: {
    label: "Język",
  },

  header: {
    encrypted: "szyfrowane end-to-end",
  },

  bytes: {
    b: "{size} B",
    kb: "{size} KB",
    mb: "{size} MB",
    gb: "{size} GB",
  },

  duration: {
    // Mianownik — etykieta sama w sobie (chip, potwierdzenie).
    standalone: {
      hours: plural({
        one: "{count} godzina",
        few: "{count} godziny",
        many: "{count} godzin",
        other: "{count} godziny",
      }),
      days: plural({ one: "{count} dzień", few: "{count} dni", many: "{count} dni", other: "{count} dnia" }),
      weeks: plural({
        one: "{count} tydzień",
        few: "{count} tygodnie",
        many: "{count} tygodni",
        other: "{count} tygodnia",
      }),
    },
    // Biernik — po „wygaśnie za …”.
    inSentence: {
      hours: plural({
        one: "{count} godzinę",
        few: "{count} godziny",
        many: "{count} godzin",
        other: "{count} godziny",
      }),
      days: plural({ one: "{count} dzień", few: "{count} dni", many: "{count} dni", other: "{count} dnia" }),
      weeks: plural({
        one: "{count} tydzień",
        few: "{count} tygodnie",
        many: "{count} tygodni",
        other: "{count} tygodnia",
      }),
    },
  },

  create: {
    mode: { text: "Tekst", file: "Plik" },
    textPlaceholder: "Wklej hasło, klucz API, klucz prywatny…",

    generator: {
      open: "Wygeneruj hasło",
      close: "Zamknij generator",
      length: "Długość",
      lowercase: "Małe litery (a–z)",
      uppercase: "Wielkie litery (A–Z)",
      digits: "Cyfry (0–9)",
      symbols: "Symbole (!@#…)",
      excludeAmbiguous: "Unikaj podobnych znaków (0/O, 1/l/I)",
      needCharset: "Wybierz co najmniej jeden rodzaj znaków.",
      generate: "Wygeneruj i wstaw",
      note: "Generowane w przeglądarce. Trafia w miejsce kursora w polu poniżej albo na koniec, jeśli kursor nie został ustawiony.",
    },

    dropzone: {
      title: "Upuść plik tutaj",
      hint: "albo {browse} — do {size}",
      browse: "wybierz z dysku",
      note: "szyfrowane, zanim opuści to urządzenie",
      remove: "Usuń",
    },

    encStatus: "Zaszyfrowane w Twojej przeglądarce. Klucz znajduje się w linku, nigdy na naszych serwerach.",

    expiry: {
      label: "Wygasa po",
      custom: "Własny",
      unitHours: "Godziny",
      unitDays: "Dni",
      unitWeeks: "Tygodnie",
    },

    oneTime: {
      title: "Zniszcz po jednym otwarciu",
      description: "Sekret zostaje wymazany w chwili otwarcia. Druga wizyta nie zobaczy już nic.",
    },

    allowedEmail: {
      label: "Ogranicz do jednego adresu e-mail",
      hint: "Każdy, kto otwiera link, potwierdza adres e-mail kodem jednorazowym. Podanie adresu tutaj sprawia, że przejdzie tylko ta osoba.",
      placeholder: "tylko-ta-osoba@przyklad.pl",
    },

    notifyEmail: {
      title: "Powiadom mnie o otwarciu",
      description:
        "Potwierdzenie odczytu wychodzi przy każdym otwarciu sekretu i wskazuje adres, który go otworzył.",
      label: "Wyślij potwierdzenie na",
      placeholder: "ty@przyklad.pl",
    },

    developer: {
      title: "Opcje dla programistów",
      webhookUrlLabel: "Adres URL webhooka",
      webhookUrlHint: "Wysyłamy zdarzenie JSON metodą POST przy utworzeniu i przy każdym otwarciu.",
      webhookSecretLabel: "Sekret podpisujący webhooka",
      webhookSecretPlaceholder: "co najmniej 16 znaków",
      webhookSecretHint:
        "Służy do podpisania ładunku (HMAC-SHA256, wysyłany w nagłówku {header}). Wymagany zawsze, gdy ustawiono adres webhooka.",
    },

    policy: {
      open: "Ten link wygaśnie za {expiry}. Każdy, kto go ma, może go otworzyć po potwierdzeniu adresu e-mail.",
      openOneTime:
        "Ten link wygaśnie za {expiry} albo w chwili otwarcia — zależnie od tego, co nastąpi wcześniej. Każdy, kto go ma, może go otworzyć po potwierdzeniu adresu e-mail.",
      restricted: "Ten link wygaśnie za {expiry}. Otworzyć go może tylko {email}.",
      restrictedOneTime:
        "Ten link wygaśnie za {expiry} albo w chwili otwarcia — zależnie od tego, co nastąpi wcześniej. Otworzyć go może tylko {email}.",
    },

    submit: "Utwórz link do sekretu",
    submitting: "Szyfrowanie…",

    error: {
      needText: "Wpisz tekst, który chcesz udostępnić.",
      needFile: "Wybierz plik, który chcesz udostępnić.",
      needNotifyEmail: "Wpisz adres dla potwierdzenia odczytu albo wyłącz powiadomienie.",
      webhookSecretTooShort: "Sekret podpisujący webhooka musi mieć co najmniej 16 znaków.",
      generic: "Coś poszło nie tak podczas tworzenia sekretu.",
    },

    result: {
      title: "Twój link jest gotowy",
      subtitle: "Wyślij go, jak tylko chcesz. Bez fragmentu po znaku kratki link jest bezużyteczny.",
      linkCaption: "część w kolorze gliny to klucz deszyfrujący — nigdy do nas nie trafia",
      expires: "Wygasa",
      destroyAfterOneView: "Zniszcz po jednym otwarciu",
      restrictedTo: "Ograniczone do",
      anyVerifiedEmail: "Dowolny potwierdzony adres",
      createAnother: "Utwórz kolejny",
    },
  },

  retrieve: {
    invalidLink: {
      title: "Nieprawidłowy link",
      body: "Temu linkowi brakuje klucza deszyfrującego. Upewnij się, że skopiowano cały adres URL — łącznie ze wszystkim po znaku {hash}.",
      note: "Jeśli ta strona została przeładowana, otwórz ponownie oryginalny link: klucz jest usuwany z paska adresu w chwili wczytania strony, więc nie trafi do historii, na zrzut ekranu ani do niczego, co ta witryna raportuje.",
    },

    notFound: {
      title: "Nie znaleziono sekretu",
      body: "Ten sekret wygasł, został już otwarty (jeśli był jednorazowy) albo nigdy nie istniał.",
    },

    alreadyRetrieved: {
      title: "Już otwarty",
      body: "To był sekret jednorazowy i ktoś już go otworzył. Nie da się go odczytać ponownie — ani przez Ciebie, ani przez nikogo innego.",
      note: "Jeśli to otwarcie pochodziło od Ciebie, a pobieranie się nie zakończyło, spróbuj ponownie w ciągu najbliższych kilku minut. Później sekret przepada na dobre, a osoba, która go wysłała, będzie musiała udostępnić go jeszcze raz.",
    },

    decrypting: "Odszyfrowywanie…",

    fatalError: {
      title: "Coś poszło nie tak",
      cannotReachServer: "Nie udało się połączyć z serwerem.",
      cannotRequestCode: "Nie udało się poprosić o kod weryfikacyjny.",
      cannotVerifyCode: "Nie udało się zweryfikować kodu.",
      decryptionFailed: "Odszyfrowanie nie powiodło się — link może być uszkodzony lub niepełny.",
    },

    email: {
      eyebrow: "Sekret czeka",
      title: "Ktoś coś Ci przesłał",
      body: "Potwierdź swój adres e-mail, aby go otworzyć. Kod jest ważny przez dziesięć minut.",
      fileLine: "Plik: {filename} ({size}). ",
      label: "Twój adres e-mail",
      placeholder: "ty@przyklad.pl",
      submit: "Wyślij kod",
      submitting: "Wysyłanie…",
      restricted: "Ten sekret jest ograniczony do konkretnego adresu e-mail, a to nie jest ten adres.",
      locked: "Zbyt wiele błędnych prób. Poproś o nowy kod.",
    },

    otp: {
      title: "Wpisz swój kod",
      body: "Wysłaliśmy jednorazowy kod na adres {email}.",
      label: "Kod jednorazowy",
      submit: "Odblokuj sekret",
      submitting: "Weryfikacja…",
      oneTimeNote: "Otwarcie go niszczy. Skopiuj to, czego potrzebujesz, zanim zamkniesz kartę.",
      resend: "Wyślij kod ponownie",
      resendIn: "Ponowne wysłanie za {seconds} s",
      incorrect: "Nieprawidłowy kod.",
      incorrectWithAttempts: plural({
        one: "Nieprawidłowy kod (została {count} próba).",
        few: "Nieprawidłowy kod (zostały {count} próby).",
        many: "Nieprawidłowy kod (zostało {count} prób).",
        other: "Nieprawidłowy kod (zostało {count} próby).",
      }),
    },

    success: {
      textTitle: "Sekret",
      fileTitle: "Twój plik",
      destroyedOnServer: "zniszczony na serwerze",
      clickToReveal: "Kliknij, aby odsłonić",
      sendOneBack: "Odeślij własny",
      fileDownloaded: "Plik {filename} został odszyfrowany w Twojej przeglądarce i pobrany.",
      destroyedNotice:
        "Ten sekret został zniszczony na serwerze. Przeładowanie tej strony nic nie pokaże. Zweryfikowano jako {email} o {time}.",
    },
  },

  reply: {
    title: "Odeślij coś",
    intro: "Trafi to do {email} i tylko ta osoba będzie mogła to otworzyć. Możesz wysłać jedną odpowiedź.",
    receiptTo: "Potwierdzenie odczytu trafi na {email}, adres zweryfikowany przed chwilą.",
    expired: {
      title: "To okno odpowiedzi zostało zamknięte",
      body: "Odpowiedź można wysłać tylko ze strony, na której otwarto sekret, i tylko przez krótki czas po tym. Poproś nadawcę o nowy link.",
    },
  },

  auth: {
    signInRequired: "Utworzenie nowego linku wymaga zalogowania.",
    expired: {
      title: "Sesja wygasła",
      body: "Nastąpiło wylogowanie, ponieważ ta strona była otwarta przez dłuższy czas. Zaloguj się ponownie, aby utworzyć link. Wpisana wcześniej treść nie została zachowana.",
    },
    signIn: "Zaloguj się",
    signedInAs: "Zalogowano jako {email}",
    signOut: "Wyloguj się",
  },

  apiError: {
    rateLimited: "Zbyt wiele żądań. Odczekaj chwilę i spróbuj ponownie.",
    tooLarge: "Ten plik jest zbyt duży, aby go udostępnić.",
    unauthenticated: "Twoja sesja wygasła. Zaloguj się ponownie, aby utworzyć link.",
    webhookHostNotPermitted: "Ten adres webhooka wskazuje miejsce, do którego ten serwer nie wysyła żądań.",
    webhooksDisabled: "Webhooki są wyłączone na tym serwerze.",
    validationFailed: "Część powyższych danych nie została przyjęta. Sprawdź je i spróbuj ponownie.",
  },
};
